import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../../config';
import { useInvoicePermissions } from '../../hooks/useInvoicePermissions';
import { useWooCommercePermissions } from '../../hooks/useWooCommercePermissions';
import { useAuth } from '../../context/AuthContext';
import './WooCommerceOrders.css';

interface WooOrder {
  _id: string;
  wooOrderId: number;
  orderNumber: string;
  status: string;
  importStatus: string;
  customerFullName: string;
  customer: {
    email: string;
    phone: string;
  };
  billing: {
    phone: string;
  };
  totals: {
    total: number;
    currency: string;
  };
  paidAmount?: number;
  paymentMethod?: string;
  dateCreated: Date;
  convertedToInvoice: boolean;
  invoiceId?: {
    _id: string;
    invoiceNumber: string;
    pageId: string;
  };
  lineItems: Array<{
    name: string;
    quantity: number;
    mappingStatus: string;
  }>;
  shippingBillNumber?: string;
  shippingBillAddedAt?: Date;
  warehouse?: {
    id?: number;
    name?: string;
    slug?: string;
  };
  warehouseMappingStatus?: string;
  smsaShipping?: {
    awbNumber?: string;
    trackingUrl?: string;
    shipmentDate?: Date;
    hasShipment?: boolean;
  };
  paymentInfo?: string;
  isMobileApp?: boolean;
  moyasarPayment?: {
    paymentSource?: string;
    cardCompany?: string;
  };
}

interface Stats {
  totalOrders: number;
  importedOrders: number;
  convertedOrders: number;
  failedOrders: number;
  totalOrderValue: number;
  totalPaidAmount: number;
  valueOrdersCount: number;
  currency: string;
}

interface InvoicePage {
  _id: string;
  name: string;
  type: string;
  description?: string;
  isActive: boolean;
}

const WooCommerceOrders: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasInvoicePagePermission } = useInvoicePermissions();
  const { hasWooCommerceOrdersPermission } = useWooCommercePermissions();
  // عرض بطاقة إجمالي قيمة الطلبات لمدير النظام فقط
  const isSystemAdmin = user?.isSystemAdmin || user?.role === 'admin';
  const [orders, setOrders] = useState<WooOrder[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    importStatus: '',
    appSource: '',
    paymentMethod: '',
    search: '',
    shippingBillNumber: '',
    invoiceNumber: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  });
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // حالة النافذة المنبثقة لاختيار صفحة الفاتورة
  const [showInvoicePageModal, setShowInvoicePageModal] = useState(false);
  const [invoicePages, setInvoicePages] = useState<InvoicePage[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [loadingPages, setLoadingPages] = useState(false);
  
  // حالة النافذة المنبثقة لبوليصة الشحن
  const [showShippingBillModal, setShowShippingBillModal] = useState(false);
  const [shippingBillNumber, setShippingBillNumber] = useState('');
  const [savingShippingBill, setSavingShippingBill] = useState(false);
  
  // حالة النافذة المنبثقة لعرض المنتجات
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<WooOrder | null>(null);

  // طرق الدفع المتاحة للفلترة
  const [paymentMethods, setPaymentMethods] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [filters]);

  useEffect(() => {
    fetchPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.status) params.append('status', filters.status);
      if (filters.importStatus) params.append('importStatus', filters.importStatus);
      if (filters.appSource) params.append('appSource', filters.appSource);
      if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
      if (filters.search) params.append('search', filters.search);
      if (filters.shippingBillNumber) params.append('shippingBillNumber', filters.shippingBillNumber);
      if (filters.invoiceNumber) params.append('invoiceNumber', filters.invoiceNumber);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      params.append('page', filters.page.toString());
      params.append('limit', filters.limit.toString());

      const response = await axios.get(
        `${API_BASE_URL}/woocommerce/orders?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setOrders(response.data.orders);
        setPagination(response.data.pagination);
      }
    } catch (error: any) {
      console.error('خطأ في جلب الطلبات:', error);
      showMessage('error', 'فشل جلب الطلبات');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();

      // تمرير كل الفلاتر الحالية لتتأثر الإحصائيات بها
      if (filters.status) params.append('status', filters.status);
      if (filters.importStatus) params.append('importStatus', filters.importStatus);
      if (filters.appSource) params.append('appSource', filters.appSource);
      if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
      if (filters.search) params.append('search', filters.search);
      if (filters.shippingBillNumber) params.append('shippingBillNumber', filters.shippingBillNumber);
      if (filters.invoiceNumber) params.append('invoiceNumber', filters.invoiceNumber);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const queryString = params.toString();
      const response = await axios.get(
        `${API_BASE_URL}/woocommerce/stats${queryString ? `?${queryString}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('خطأ في جلب الإحصائيات:', error);
    }
  };

  // جلب طرق الدفع المتاحة للفلترة
  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/woocommerce/payment-methods`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPaymentMethods(response.data.methods || []);
      }
    } catch (error) {
      console.error('خطأ في جلب طرق الدفع:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/woocommerce/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', `تمت المزامنة بنجاح! تم استيراد ${response.data.stats.newOrders} طلب جديد`);
        fetchOrders();
        fetchStats();
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'فشلت المزامنة');
    } finally {
      setSyncing(false);
    }
  };

  // فتح نافذة اختيار صفحة الفاتورة
  const handleConvertToInvoice = async (orderId: string) => {
    setSelectedOrderId(orderId);
    setShowInvoicePageModal(true);
    await fetchInvoicePages();
  };

  // جلب صفحات الفواتير المتاحة
  const fetchInvoicePages = async () => {
    setLoadingPages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/invoices/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('استجابة صفحات الفواتير الكاملة:', response.data);

      // التعامل مع بنية الاستجابة المختلفة
      let pages: any[] = [];
      
      if (response.data.success && response.data.data) {
        // البنية: {success: true, data: {pages: [...], ...}}
        if (Array.isArray(response.data.data.pages)) {
          pages = response.data.data.pages;
        }
        // البنية: {success: true, data: [...]}
        else if (Array.isArray(response.data.data)) {
          pages = response.data.data;
        }
      }
      // البنية: {success: true, pages: [...]}
      else if (response.data.success && Array.isArray(response.data.pages)) {
        pages = response.data.pages;
      }
      // البنية: [...]
      else if (Array.isArray(response.data)) {
        pages = response.data;
      } else {
        console.warn('بنية استجابة غير متوقعة:', response.data);
      }

      console.log('الصفحات المستخرجة:', pages);

      // التحقق من أن pages مصفوفة قبل الفلترة
      if (!Array.isArray(pages)) {
        console.error('البيانات المستلمة ليست مصفوفة:', pages);
        setInvoicePages([]);
        return;
      }

      // فلترة الصفحات النشطة وصفحات فواتير المبيعات فقط والتي يملك المستخدم صلاحية الوصول إليها
      // التحقق من الخاصية isActive أو active وأن نوع الصفحة هو sales_invoice
      const activePages = pages.filter((page: any) => {
        // بعض الصفحات قد تستخدم isActive وبعضها active
        const isActive = page.isActive === true || page.active === true || page.isActive !== false;
        // التحقق من أن نوع الصفحة هو فاتورة مبيعات
        const isSalesInvoice = page.type === 'sales_invoice';
        // التحقق من صلاحية المستخدم للوصول إلى هذه الصفحة
        // استخدام sequentialId إذا كان موجوداً، وإلا استخدام _id
        const pageIdentifier = page.sequentialId || page._id;
        const hasPermission = hasInvoicePagePermission(pageIdentifier, 'view_page');
        
        console.log(`التحقق من صلاحية الصفحة: ${page.name} (${pageIdentifier}) - النتيجة: ${hasPermission}`);
        
        return isActive && isSalesInvoice && hasPermission;
      });
      
      console.log('الصفحات النشطة (فواتير مبيعات فقط مع صلاحيات):', activePages);
      console.log('عينة من صفحة:', pages[0]); // لرؤية بنية الصفحة
      
      // عرض صفحات فواتير المبيعات النشطة التي يملك المستخدم صلاحية الوصول إليها فقط
      setInvoicePages(activePages);
    } catch (error: any) {
      console.error('خطأ في جلب صفحات الفواتير:', error);
      showMessage('error', 'فشل جلب صفحات الفواتير');
      setInvoicePages([]);
    } finally {
      setLoadingPages(false);
    }
  };

  // تأكيد التحويل بعد اختيار الصفحة
  const confirmConvertToInvoice = async (invoicePageId: string) => {
    if (!selectedOrderId) return;

    try {
      const token = localStorage.getItem('token');
      
      // تحضير بيانات الفاتورة
      const response = await axios.post(
        `${API_BASE_URL}/woocommerce/orders/${selectedOrderId}/prepare-invoice`,
        { invoicePageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // إغلاق النافذة المنبثقة
        setShowInvoicePageModal(false);
        setSelectedOrderId(null);
        
        // الانتقال إلى صفحة إنشاء الفاتورة المحددة مع البيانات المحضرة
        navigate(`/invoices/${invoicePageId}/new`, {
          state: {
            fromWooCommerce: true,
            wooOrderId: selectedOrderId,
            invoiceData: response.data.invoiceData,
            warnings: response.data.warnings
          }
        });
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'فشل تحضير الفاتورة');
    }
  };

  // إغلاق النافذة المنبثقة لاختيار صفحة الفاتورة
  const closeModal = () => {
    setShowInvoicePageModal(false);
    setSelectedOrderId(null);
  };

  // فتح نافذة إضافة بوليصة الشحن
  const handleAddShippingBill = (orderId: string) => {
    setSelectedOrderId(orderId);
    
    // البحث عن الطلب للحصول على رقم البوليصة SMSA إن وجد
    const order = orders.find(o => o._id === orderId);
    const smsaAwbNumber = order?.smsaShipping?.awbNumber || '';
    
    setShippingBillNumber(smsaAwbNumber);
    setShowShippingBillModal(true);
  };

  // حفظ بوليصة الشحن
  const saveShippingBill = async () => {
    if (!selectedOrderId || !shippingBillNumber.trim()) {
      showMessage('error', 'يرجى إدخال رقم بوليصة الشحن');
      return;
    }

    setSavingShippingBill(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/woocommerce/orders/${selectedOrderId}/shipping-bill`,
        { shippingBillNumber: shippingBillNumber.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'تم حفظ بوليصة الشحن بنجاح');
        setShowShippingBillModal(false);
        setSelectedOrderId(null);
        setShippingBillNumber('');
        fetchOrders(); // تحديث قائمة الطلبات
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'فشل حفظ بوليصة الشحن');
    } finally {
      setSavingShippingBill(false);
    }
  };

  // إغلاق نافذة بوليصة الشحن
  const closeShippingBillModal = () => {
    setShowShippingBillModal(false);
    setSelectedOrderId(null);
    setShippingBillNumber('');
  };

  // حذف بوليصة الشحن
  const handleDeleteShippingBill = async (orderId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف بوليصة الشحن؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE_URL}/woocommerce/orders/${orderId}/shipping-bill`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'تم حذف بوليصة الشحن بنجاح');
        fetchOrders(); // تحديث قائمة الطلبات
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'فشل حذف بوليصة الشحن');
    }
  };

  // إلغاء ربط الفاتورة
  const handleUnlinkInvoice = async (orderId: string) => {
    if (!window.confirm('هل أنت متأكد من إلغاء ربط الفاتورة؟ لن يتم حذف الفاتورة الأصلية.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE_URL}/woocommerce/orders/${orderId}/unlink-invoice`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'تم إلغاء ربط الفاتورة بنجاح');
        fetchOrders(); // تحديث قائمة الطلبات
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'فشل إلغاء ربط الفاتورة');
    }
  };

  // فتح نافذة عرض المنتجات
  const handleViewProducts = (order: WooOrder) => {
    setSelectedOrder(order);
    setShowProductsModal(true);
  };

  // إغلاق نافذة عرض المنتجات
  const closeProductsModal = () => {
    setShowProductsModal(false);
    setSelectedOrder(null);
  };

  // حذف طلب واحد
  const handleDeleteOrder = async (orderId: string, orderNumber: string) => {
    if (!window.confirm(`هل أنت متأكد من حذف الطلب #${orderNumber}؟ هذا الإجراء لا يمكن التراجع عنه.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE_URL}/woocommerce/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', `تم حذف الطلب #${orderNumber} بنجاح`);
        fetchOrders(); // تحديث قائمة الطلبات
        fetchStats(); // تحديث الإحصائيات
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'فشل حذف الطلب');
    }
  };

  // حذف جميع الطلبات
  const handleDeleteAllOrders = async () => {
    if (!window.confirm('⚠️ هل أنت متأكد من حذف جميع الطلبات؟\n\nهذا الإجراء سيحذف جميع طلبات WooCommerce من قاعدة البيانات ولا يمكن التراجع عنه!\n\nاضغط "موافق" للمتابعة أو "إلغاء" للرجوع.')) {
      return;
    }

    // تأكيد إضافي
    if (!window.confirm('⚠️⚠️ تأكيد نهائي!\n\nأنت على وشك حذف جميع الطلبات نهائياً.\n\nهل أنت متأكد تماماً؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE_URL}/woocommerce/orders/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', `تم حذف ${response.data.deletedCount} طلب بنجاح`);
        fetchOrders(); // تحديث قائمة الطلبات
        fetchStats(); // تحديث الإحصائيات
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'فشل حذف الطلبات');
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      importStatus: '',
      appSource: '',
      paymentMethod: '',
      search: '',
      shippingBillNumber: '',
      invoiceNumber: '',
      dateFrom: '',
      dateTo: '',
      page: 1,
      limit: 20
    });
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // تصدير الطلبات إلى ملف Excel وفق الفلاتر الحالية (كل الصفحات)
  const handleExportExcel = async () => {
    setExporting(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');

      // بناء باراميترات الفلاتر الحالية
      const buildParams = () => {
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.importStatus) params.append('importStatus', filters.importStatus);
        if (filters.appSource) params.append('appSource', filters.appSource);
        if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
        if (filters.search) params.append('search', filters.search);
        if (filters.shippingBillNumber) params.append('shippingBillNumber', filters.shippingBillNumber);
        if (filters.invoiceNumber) params.append('invoiceNumber', filters.invoiceNumber);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        return params;
      };

      // جلب كل الصفحات المطابقة للفلاتر
      const exportLimit = 100;
      let currentPage = 1;
      let totalPages = 1;
      let allOrders: WooOrder[] = [];

      do {
        const params = buildParams();
        params.append('page', currentPage.toString());
        params.append('limit', exportLimit.toString());

        const response = await axios.get(
          `${API_BASE_URL}/woocommerce/orders?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.data.success) break;

        allOrders = allOrders.concat(response.data.orders || []);
        totalPages = response.data.pagination?.pages || 1;
        currentPage++;
      } while (currentPage <= totalPages);

      if (allOrders.length === 0) {
        showMessage('error', 'لا توجد طلبات للتصدير');
        return;
      }

      // تحويل البيانات إلى صفوف إكسل
      const excelData = allOrders.map((order) => ({
        'رقم الطلب': order.orderNumber || '',
        'اسم العميل': order.customerFullName || '',
        'البريد الإلكتروني': order.customer?.email || '',
        'رقم الهاتف': order.billing?.phone || order.customer?.phone || '',
        'عدد المنتجات': (order.lineItems || []).reduce((total, item) => total + (item.quantity || 0), 0),
        'الإجمالي': order.totals?.total || 0,
        'العملة': order.totals?.currency || 'SAR',
        'المبلغ المدفوع': order.paidAmount || 0,
        'طريقة الدفع': order.paymentInfo || '',
        'التطبيق': order.isMobileApp ? 'تطبيق الجوال' : 'لا',
        'حالة الطلب': order.status || '',
        'حالة الاستيراد': order.importStatus || '',
        'بوليصة الشحن': order.shippingBillNumber || '',
        'رقم الفاتورة': order.invoiceId?.invoiceNumber || '',
        'تاريخ الطلب': order.dateCreated ? new Date(order.dateCreated).toLocaleString('ar-SA') : ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      // ضبط عرض الأعمدة
      worksheet['!cols'] = [
        { wch: 15 }, { wch: 25 }, { wch: 25 }, { wch: 18 }, { wch: 12 },
        { wch: 12 }, { wch: 8 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
        { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 22 }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'طلبات ووكمرس');

      const fileName = `طلبات_ووكمرس_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      showMessage('success', `تم تصدير ${allOrders.length} طلب بنجاح`);
    } catch (error: any) {
      console.error('خطأ في تصدير الطلبات:', error);
      showMessage('error', 'فشل تصدير الطلبات إلى Excel');
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { label: string; className: string } } = {
      'pending': { label: 'قيد الانتظار', className: 'status-pending' },
      'processing': { label: 'قيد المعالجة', className: 'status-processing' },
      'on-hold': { label: 'معلق', className: 'status-on-hold' },
      'completed': { label: 'مكتمل', className: 'status-completed' },
      'shipped': { label: 'تم الشحن', className: 'status-shipped' },
      'cancelled': { label: 'ملغي', className: 'status-cancelled' },
      'refunded': { label: 'مسترد', className: 'status-refunded' },
      'failed': { label: 'فاشل', className: 'status-failed' }
    };

    const statusInfo = statusMap[status] || { label: status, className: 'status-default' };
    return <span className={`status-badge ${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  const getImportStatusBadge = (importStatus: string) => {
    const statusMap: { [key: string]: { label: string; className: string } } = {
      'imported': { label: 'مستورد', className: 'import-status-imported' },
      'converted': { label: 'محول', className: 'import-status-converted' },
      'failed': { label: 'فشل', className: 'import-status-failed' }
    };

    const statusInfo = statusMap[importStatus] || { label: importStatus, className: 'import-status-default' };
    return <span className={`import-status-badge ${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  return (
    <div className="woocommerce-orders-container">
      {/* Header */}
      <div className="orders-header">
        <div className="header-content">
          <h2>🛒 طلبات WooCommerce</h2>
          <div className="header-actions">
            {isSystemAdmin && (
              <button
                onClick={handleExportExcel}
                disabled={exporting || orders.length === 0}
                className="btn btn-export"
                style={{
                  backgroundColor: '#1d6f42',
                  color: 'white',
                  border: 'none',
                  marginRight: '10px',
                  opacity: exporting || orders.length === 0 ? 0.6 : 1,
                  cursor: exporting || orders.length === 0 ? 'not-allowed' : 'pointer'
                }}
                title="تصدير الطلبات إلى ملف Excel"
              >
                {exporting ? '⏳ جاري التصدير...' : '📊 تصدير Excel'}
              </button>
            )}
            {hasWooCommerceOrdersPermission('sync_orders') && (
              <button
                onClick={handleSync}
                disabled={syncing}
                className="btn btn-primary"
              >
                {syncing ? '⏳ جاري المزامنة...' : '🔄 مزامنة الآن'}
              </button>
            )}
            {hasWooCommerceOrdersPermission('delete_all_orders') && orders.length > 0 && (
              <button
                onClick={handleDeleteAllOrders}
                className="btn btn-danger"
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  marginRight: '10px'
                }}
              >
                🗑️ حذف جميع الطلبات
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-info">
              <div className="stat-value">{stats.totalOrders}</div>
              <div className="stat-label">إجمالي الطلبات</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📥</div>
            <div className="stat-info">
              <div className="stat-value">{stats.importedOrders}</div>
              <div className="stat-label">طلبات مستوردة</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <div className="stat-value">{stats.convertedOrders}</div>
              <div className="stat-label">طلبات محولة</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">❌</div>
            <div className="stat-info">
              <div className="stat-value">{stats.failedOrders}</div>
              <div className="stat-label">طلبات فاشلة</div>
            </div>
          </div>
          {isSystemAdmin && (
            <div className="stat-card stat-card-value">
              <div className="stat-icon">💰</div>
              <div className="stat-info">
                <div className="stat-value">
                  {(stats.totalOrderValue || 0).toLocaleString('ar-SA', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} {stats.currency || 'SAR'}
                </div>
                <div className="stat-label">
                  إجمالي قيمة الطلبات
                  {filters.dateFrom || filters.dateTo ? (
                    <span className="stat-sub-label">
                      📅 {filters.dateFrom ? `من ${filters.dateFrom}` : ''}
                      {filters.dateTo ? ` إلى ${filters.dateTo}` : ''}
                    </span>
                  ) : (
                    <span className="stat-sub-label">📅 كل الفترات</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <input
              type="text"
              placeholder="🔍 بحث برقم الطلب، البريد، أو الهاتف..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <input
              type="text"
              placeholder="📦 رقم بوليصة الشحن..."
              value={filters.shippingBillNumber}
              onChange={(e) => handleFilterChange('shippingBillNumber', e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <input
              type="text"
              placeholder="📄 رقم الفاتورة..."
              value={filters.invoiceNumber}
              onChange={(e) => handleFilterChange('invoiceNumber', e.target.value)}
              className="search-input"
            />
          </div>
          <button
            onClick={handleClearFilters}
            className="btn-clear-filters"
            title="مسح جميع الفلاتر"
          >
            🗑️ مسح الفلاتر
          </button>
        </div>
        <div className="filters-row">
          <div className="filter-group">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">كل الحالات</option>
              <option value="pending">قيد الانتظار</option>
              <option value="processing">قيد المعالجة</option>
              <option value="on-hold">معلق</option>
              <option value="completed">مكتمل</option>
              <option value="shipped">تم الشحن</option>
              <option value="cancelled">ملغي</option>
            </select>
          </div>
          <div className="filter-group">
            <select
              value={filters.importStatus}
              onChange={(e) => handleFilterChange('importStatus', e.target.value)}
              className="filter-select"
            >
              <option value="">كل حالات الاستيراد</option>
              <option value="imported">مستورد</option>
              <option value="converted">محول</option>
              <option value="failed">فشل</option>
            </select>
          </div>
          <div className="filter-group">
            <select
              value={filters.appSource}
              onChange={(e) => handleFilterChange('appSource', e.target.value)}
              className="filter-select"
              title="مصدر الطلب"
            >
              <option value="">كل المصادر</option>
              <option value="mobile">تطبيق الجوال</option>
              <option value="web">ويب</option>
            </select>
          </div>
          <div className="filter-group">
            <select
              value={filters.paymentMethod}
              onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
              className="filter-select"
              title="طريقة الدفع"
            >
              <option value="">كل طرق الدفع</option>
              {paymentMethods.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <input
              type="date"
              placeholder="من تاريخ"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="filter-select"
              title="من تاريخ"
            />
          </div>
          <div className="filter-group">
            <input
              type="date"
              placeholder="إلى تاريخ"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="filter-select"
              title="إلى تاريخ"
            />
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="orders-table-container">
        {loading ? (
          <div className="loading-spinner">⏳ جاري التحميل...</div>
        ) : orders.length === 0 ? (
          <div className="no-orders">
            <p>📭 لا توجد طلبات</p>
            {hasWooCommerceOrdersPermission('sync_orders') && (
              <button onClick={handleSync} className="btn btn-primary">
                🔄 ابدأ المزامنة
              </button>
            )}
          </div>
        ) : (
          <table className="orders-table">
            <thead>
              <tr>
                <th>رقم الطلب</th>
                <th>العميل</th>
                <th>المنتجات</th>
                <th>الإجمالي</th>
                <th>المدفوع</th>
                <th>طريقة الدفع</th>
                <th>التطبيق</th>
                <th>الحالة</th>
                <th>حالة الاستيراد</th>
                <th>بوليصة الشحن</th>
                <th>رقم الفاتورة</th>
                <th>التاريخ</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order._id}>
                  <td className="order-number">#{order.orderNumber}</td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">{order.customerFullName}</div>
                      <div className="customer-contact">
                        {order.billing.phone || order.customer.phone || '-'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="products-info">
                      <span>{order.lineItems.reduce((total, item) => total + item.quantity, 0)} كمية</span>
                      <button
                        onClick={() => handleViewProducts(order)}
                        className="btn-icon btn-view-products"
                        title="مشاهدة المنتجات"
                      >
                        👁️
                      </button>
                    </div>
                  </td>
                  <td className="order-total">
                    {order.totals.total.toFixed(2)} {order.totals.currency}
                  </td>
                  <td className="paid-amount">
                    {order.paidAmount ? order.paidAmount.toFixed(2) : '0.00'} {order.totals.currency}
                  </td>
                  <td className="payment-info">
                    {order.paymentInfo || '-'}
                  </td>
                  <td>
                    {order.isMobileApp ? (
                      <span className="app-source-badge app-source-mobile">📱 تطبيق الجوال</span>
                    ) : (
                      <span className="app-source-badge app-source-web">لا</span>
                    )}
                  </td>
                  <td>{getStatusBadge(order.status)}</td>
                  <td>{getImportStatusBadge(order.importStatus)}</td>
                  <td>
                    {order.shippingBillNumber ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="shipping-bill-number" title={`تم الإضافة: ${order.shippingBillAddedAt ? new Date(order.shippingBillAddedAt).toLocaleDateString('ar-SA') : ''}`}>
                          📦 {order.shippingBillNumber}
                        </span>
                        {hasWooCommerceOrdersPermission('delete_shipping_bill') && (
                          <button
                            onClick={() => handleDeleteShippingBill(order._id)}
                            className="btn-icon btn-delete"
                            title="حذف بوليصة الشحن"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="no-shipping-bill">-</span>
                    )}
                  </td>
                  <td>
                    {order.invoiceId ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="invoice-number">
                          {order.invoiceId.invoiceNumber}
                        </span>
                        {hasWooCommerceOrdersPermission('unlink_invoice') && (
                          <button
                            onClick={() => handleUnlinkInvoice(order._id)}
                            className="btn-icon btn-unlink"
                            title="إلغاء ربط الفاتورة"
                          >
                            🔗❌
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="no-invoice">-</span>
                    )}
                  </td>
                  <td className="order-date">
                    {new Date(order.dateCreated).toLocaleDateString('ar-SA')}
                  </td>
                  <td className="order-actions">
                    {order.convertedToInvoice && order.invoiceId ? (
                      <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                        <button
                          onClick={() => {
                            if (order.invoiceId) {
                              navigate(`/invoices/${order.invoiceId.pageId}/${order.invoiceId._id}/view`);
                            }
                          }}
                          className="btn btn-sm btn-view"
                        >
                          📄 عرض الفاتورة
                        </button>
                        {hasWooCommerceOrdersPermission('delete_order') && (
                          <button
                            onClick={() => handleDeleteOrder(order._id, order.orderNumber)}
                            className="btn btn-sm btn-delete"
                            style={{
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            🗑️ حذف
                          </button>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                        {!order.shippingBillNumber && hasWooCommerceOrdersPermission('add_shipping_bill') && (
                          <button
                            onClick={() => handleAddShippingBill(order._id)}
                            className="btn btn-sm btn-shipping"
                            disabled={(order.paidAmount || 0) < order.totals.total && order.paymentMethod !== 'cod'}
                            title={(order.paidAmount || 0) < order.totals.total && order.paymentMethod !== 'cod' ? 'لا يمكن إضافة بوليصة: المبلغ المدفوع أقل من الإجمالي' : ''}
                          >
                            📦 إضافة بوليصة
                          </button>
                        )}
                        {hasWooCommerceOrdersPermission('convert_to_invoice') && (
                          <button
                            onClick={() => handleConvertToInvoice(order._id)}
                            className="btn btn-sm btn-convert"
                            disabled={!order.shippingBillNumber}
                            title={!order.shippingBillNumber ? 'يجب إضافة بوليصة الشحن أولاً' : ''}
                          >
                            ➡️ تحويل لفاتورة
                          </button>
                        )}
                        {hasWooCommerceOrdersPermission('delete_order') && (
                          <button
                            onClick={() => handleDeleteOrder(order._id, order.orderNumber)}
                            className="btn btn-sm btn-delete"
                            style={{
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            🗑️ حذف
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="btn btn-sm"
          >
            السابق
          </button>
          <span className="page-info">
            صفحة {pagination.page} من {pagination.pages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.pages}
            className="btn btn-sm"
          >
            التالي
          </button>
        </div>
      )}

      {/* نافذة اختيار صفحة الفاتورة */}
      {showInvoicePageModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📄 اختر صفحة الفاتورة</h3>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            
            <div className="modal-body">
              {loadingPages ? (
                <div className="loading-spinner">⏳ جاري التحميل...</div>
              ) : invoicePages.length === 0 ? (
                <div className="no-pages">
                  <p>❌ لا توجد صفحات فواتير نشطة</p>
                  <p className="hint">يرجى إنشاء صفحة فاتورة أولاً من إعدادات النظام</p>
                </div>
              ) : (
                <div className="invoice-pages-grid">
                  {invoicePages.map((page) => (
                    <div
                      key={page._id}
                      className="invoice-page-card"
                      onClick={() => confirmConvertToInvoice(page._id)}
                    >
                      <div className="page-icon">
                        {page.type === 'sales_invoice' && '🛒'}
                        {page.type === 'sales_return' && '↩️'}
                        {page.type === 'purchase_invoice' && '📦'}
                        {page.type === 'purchase_return' && '📤'}
                      </div>
                      <div className="page-info">
                        <h4>{page.name}</h4>
                        {page.description && (
                          <p className="page-description">{page.description}</p>
                        )}
                        <span className="page-type">
                          {page.type === 'sales_invoice' && 'فاتورة مبيعات'}
                          {page.type === 'sales_return' && 'مرتجع مبيعات'}
                          {page.type === 'purchase_invoice' && 'فاتورة مشتريات'}
                          {page.type === 'purchase_return' && 'مرتجع مشتريات'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة إضافة بوليصة الشحن */}
      {showShippingBillModal && (
        <div className="modal-overlay" onClick={closeShippingBillModal}>
          <div className="modal-content shipping-bill-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📦 إضافة بوليصة الشحن</h3>
              <button className="modal-close" onClick={closeShippingBillModal}>✕</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="shippingBillNumber">رقم بوليصة الشحن *</label>
                <input
                  type="text"
                  id="shippingBillNumber"
                  className="form-input"
                  placeholder="أدخل رقم بوليصة الشحن"
                  value={shippingBillNumber}
                  onChange={(e) => setShippingBillNumber(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !savingShippingBill) {
                      saveShippingBill();
                    }
                  }}
                  autoFocus
                />
                <p className="form-hint">يرجى إدخال رقم بوليصة الشحن الخاصة بهذا الطلب</p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={closeShippingBillModal}
                disabled={savingShippingBill}
              >
                إلغاء
              </button>
              <button
                className="btn btn-primary"
                onClick={saveShippingBill}
                disabled={savingShippingBill || !shippingBillNumber.trim()}
              >
                {savingShippingBill ? '⏳ جاري الحفظ...' : '💾 حفظ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة عرض المنتجات */}
      {showProductsModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeProductsModal}>
          <div className="modal-content products-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📦 منتجات الطلب #{selectedOrder.orderNumber}</h3>
              <button className="modal-close" onClick={closeProductsModal}>✕</button>
            </div>
            
            <div className="modal-body">
              {/* معلومات المستودع */}
              {selectedOrder.warehouse && selectedOrder.warehouse.name && (
                <div className="warehouse-info" style={{
                  padding: '12px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  border: '1px solid #bfdbfe'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>🏪</span>
                    <div>
                      <strong style={{ color: '#1e40af' }}>المستودع:</strong>
                      <span style={{ marginRight: '8px', color: '#1e3a8a' }}>
                        {selectedOrder.warehouse.name}
                      </span>
                      {selectedOrder.warehouse.id && (
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                          (ID: {selectedOrder.warehouse.id})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="products-list">
                {selectedOrder.lineItems.map((item, index) => (
                  <div key={index} className="product-item">
                    <div className="product-details">
                      <div className="product-name">{item.name}</div>
                      <div className="product-quantity">الكمية: {item.quantity}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="products-summary">
                <strong>إجمالي المنتجات:</strong> {selectedOrder.lineItems.length}
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeProductsModal}>
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WooCommerceOrders;
